import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import RafagaGrupoIcono from '../../../components/RafagaGrupoIcono';
import { claveGrupo } from '../../dashboard/GruposRafaga';

const dragType = 'application/x-rafaga-servicio';

export default function AsignadorRafaga({ servicios, accesos, setAccesos, grupos, disabled }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [over, setOver] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const disponibles = servicios.filter((s) => !accesos.some((a) => Number(a.id_servicio) === Number(s.id_servicio)));
  const filtrados = disponibles.filter((s) => `${s.nombre} ${s.codigo || ''} ${s.categoria || ''}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));

  const mover = (id, destino) => {
    if (disabled) return;
    const actual = accesos.find((a) => Number(a.id_servicio) === Number(id));
    const servicio = servicios.find((s) => Number(s.id_servicio) === Number(id));
    if (!actual && !servicio) return;
    if (destino && !grupos.some((g) => g.clave === destino)) return;
    if (destino && !actual && accesos.length >= 100) {
      setError('Puedes asignar hasta 100 botones a la Zona de Ráfaga.'); return;
    }
    setAccesos((prev) => {
      if (!destino) return prev.filter((a) => Number(a.id_servicio) !== Number(id));
      if (prev.some((a) => Number(a.id_servicio) === Number(id))) {
        return prev.map((a) => Number(a.id_servicio) === Number(id) ? { ...a, grupo_clave: destino } : a);
      }
      if (prev.length >= 100) return prev;
      return [...prev, { id_servicio: servicio.id_servicio, etiqueta: servicio.nombre, grupo: 'TRAMITES', grupo_clave: destino, color: '#2563eb' }];
    });
    setSelected(null); setOver(null); setError('');
    setFeedback(`${actual?.etiqueta || servicio?.nombre}: ${destino ? grupos.find((g) => g.clave === destino).nombre : 'sin asignar'}. Guarda la Zona de Ráfaga para aplicar los cambios.`);
  };
  const dropProps = (destino) => ({
    onDragOver: (e) => {
      if (disabled || !Array.from(e.dataTransfer.types || []).includes(dragType)) return;
      e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setOver(destino);
    },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation(); setOver(null);
      const id = e.dataTransfer.getData(dragType);
      if (id) mover(id, destino === 'sin-asignar' ? null : destino);
    },
  });
  const ficha = (s, asignado = false) => {
    const id = Number(s.id_servicio);
    const nombre = asignado ? s.etiqueta : s.nombre;
    return <Chip key={id} label={nombre} draggable={!disabled} disabled={disabled}
      aria-label={`${asignado ? 'Asignado' : 'Servicio'}: ${nombre}`}
      variant={selected === id ? 'filled' : 'outlined'} color={selected === id ? 'primary' : 'default'}
      onClick={() => setSelected(selected === id ? null : id)}
      onDoubleClick={() => { if (asignado) setEditing({ ...s }); }}
      onDelete={asignado ? () => mover(id, null) : undefined}
      onDragStart={(e) => { e.dataTransfer.setData(dragType, String(id)); e.dataTransfer.effectAllowed = 'move'; setSelected(id); }}
      onDragEnd={() => { setOver(null); setSelected(null); }}
      sx={{ maxWidth:'100%', cursor:disabled ? 'default' : 'grab', '& .MuiChip-label':{whiteSpace:'normal'}, height:'auto', minHeight:34, py:0.5 }} />;
  };
  const zona = (g) => {
    const propios = accesos.filter((a) => claveGrupo(a) === g.clave);
    return <Paper key={g.clave} component="section" aria-label={`Grupo ${g.nombre}`} variant="outlined" {...dropProps(g.clave)}
      sx={{ p:1.5, borderRadius:2, borderStyle:'dashed', borderColor:over === g.clave ? 'primary.main' : 'divider', bgcolor:over === g.clave ? 'action.hover' : 'background.paper', transition:'background-color 180ms, border-color 180ms', minWidth:0 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <RafagaGrupoIcono grupo={g} size={28} /><Typography fontWeight={700} sx={{flex:1}}>{g.nombre}</Typography>
        <Typography variant="caption">{propios.length}</Typography>
      </Stack>
      <Button fullWidth size="small" disabled={disabled || selected === null} onClick={() => mover(selected,g.clave)} aria-label={`Colocar servicio en ${g.nombre}`}>
        {selected === null ? 'Arrastra aquí los servicios' : 'Colocar servicio seleccionado aquí'}
      </Button>
      <Box sx={{display:'flex',flexWrap:'wrap',gap:1,mt:1,minHeight:42}}>{propios.map((a) => <Stack key={a.id_servicio} direction="row" alignItems="center" sx={{maxWidth:'100%'}}>
        {ficha(a,true)}<Button size="small" disabled={disabled} aria-label={`Editar botón ${a.etiqueta}`} onClick={() => setEditing({...a})}>Editar</Button>
      </Stack>)}</Box>
    </Paper>;
  };
  return <Stack spacing={2}>
    <Typography color="text.secondary">Arrastra cada servicio a su grupo o subgrupo. También puedes tocar un servicio y después «Colocar servicio seleccionado aquí». Para quitarlo, usa la X o devuélvelo a «Sin asignar».</Typography>
    {error && <Alert severity="warning">{error}</Alert>}
    <Typography role="status" variant="body2" color="text.secondary">{feedback}</Typography>
    <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',lg:'minmax(240px,1fr) minmax(0,2fr)'},gap:2,alignItems:'start'}}>
      <Paper component="section" aria-label="Servicios sin asignar" variant="outlined" {...dropProps('sin-asignar')}
        sx={{p:2,borderRadius:2,borderStyle:'dashed',borderColor:over === 'sin-asignar' ? 'primary.main' : 'divider',bgcolor:over === 'sin-asignar' ? 'action.hover' : 'background.paper'}}>
        <Typography fontWeight={700} mb={1}>Sin asignar ({disponibles.length})</Typography>
        <TextField fullWidth size="small" label="Buscar servicio sin asignar" value={query} onChange={(e) => setQuery(e.target.value)} disabled={disabled} />
        <Box sx={{display:'flex',flexWrap:'wrap',gap:1,mt:2,maxHeight:540,overflowY:'auto',minHeight:70}}>
          {filtrados.map((s) => ficha(s))}
          {!filtrados.length && <Typography variant="body2" color="text.secondary">{query ? 'No hay coincidencias.' : 'Todos los servicios disponibles están asignados.'}</Typography>}
        </Box>
      </Paper>
      <Stack spacing={2}>
        {!grupos.length && <Alert severity="info">Crea un grupo o subgrupo arriba para empezar a organizar los botones.</Alert>}
        {grupos.filter((g) => !g.padre).map((g) => <Stack key={g.clave} spacing={1.5}>
          {zona(g)}
          <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',md:'repeat(2,minmax(0,1fr))'},gap:1.5,pl:2}}>
            {grupos.filter((s) => s.padre === g.clave).map(zona)}
          </Box>
        </Stack>)}
      </Stack>
    </Box>
    <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="xs">
      <DialogTitle>Editar botón</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{pt:1}}>
        <TextField label="Texto del botón" value={editing?.etiqueta || ''} inputProps={{maxLength:150}} onChange={(e) => setEditing((a) => ({...a,etiqueta:e.target.value}))} />
        <TextField select label="Tamaño" value={editing?.grupo || 'TRAMITES'} onChange={(e) => setEditing((a) => ({...a,grupo:e.target.value}))}>
          <MenuItem value="PRINCIPAL">Grande</MenuItem><MenuItem value="TRAMITES">Compacto</MenuItem>
        </TextField>
        <TextField type="color" label="Color" value={editing?.color || '#2563eb'} onChange={(e) => setEditing((a) => ({...a,color:e.target.value}))} />
        <Button disabled={disabled} onClick={() => setAccesos((prev) => {
          const index = prev.findIndex((a) => a.id_servicio === editing.id_servicio);
          const anterior = prev.slice(0,index).map((a,i) => ({a,i})).filter(({a}) => claveGrupo(a) === claveGrupo(editing)).pop();
          if (!anterior) return prev;
          const next = [...prev]; [next[anterior.i],next[index]] = [next[index],next[anterior.i]]; return next;
        })}>Subir orden dentro del grupo</Button>
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setEditing(null)}>Cerrar</Button><Button disabled={disabled || !editing?.etiqueta.trim()} onClick={() => {
        setAccesos((prev) => prev.map((a) => a.id_servicio === editing.id_servicio ? {...a,etiqueta:editing.etiqueta,grupo:editing.grupo,color:editing.color} : a)); setEditing(null);
      }}>Aplicar</Button></DialogActions>
    </Dialog>
  </Stack>;
}
